import Phaser from 'phaser';
import { MENU_ITEMS } from '../../data/menu';
import {
  createProductionCharacterSprite,
  setProductionCharacterDirection,
  setProductionCharacterState,
  type CharacterDirection,
} from '../characters/CharacterArt';
import type { CustomerArchetype, CustomerState, MenuItemId, TableState } from '../types';
import {
  createProductionCustomerSprite,
  setProductionCustomerDirection,
  setProductionCustomerState,
  type CustomerVisualId,
} from '../customers/CustomerArt';

export interface TableVisual {
  container: Phaser.GameObjects.Container;
  glow: Phaser.GameObjects.Ellipse;
  surface: Phaser.GameObjects.Ellipse;
  stateBadge: Phaser.GameObjects.Container;
  statePlate: Phaser.GameObjects.Graphics;
  stateIcon: Phaser.GameObjects.Text;
  stateText: Phaser.GameObjects.Text;
  crumbs: Phaser.GameObjects.Container;
}

const UI_FONT = 'Arial, sans-serif';

function createNameBadge(scene: Phaser.Scene, label: string, y: number): Phaser.GameObjects.Container {
  const width = Math.max(54, label.length * 9 + 18);
  const shadow = scene.add.graphics().fillStyle(0x091f1d, 0.32).fillRoundedRect(-width / 2, -10, width, 24, 8);
  const plate = scene.add.graphics()
    .fillStyle(0x163f3a, 0.98).lineStyle(1, 0xe8c575, 0.72)
    .fillRoundedRect(-width / 2, -13, width, 23, 8).strokeRoundedRect(-width / 2, -13, width, 23, 8);
  const text = scene.add.text(0, -2, label, {
    fontFamily: UI_FONT, fontSize: '11px', fontStyle: 'bold', color: '#fff1cb', letterSpacing: 1,
  }).setOrigin(0.5);
  return scene.add.container(0, y, [shadow, plate, text]);
}

function createFace(scene: Phaser.Scene, skin: number, y: number): Phaser.GameObjects.Container {
  const headShadow = scene.add.circle(2, y + 3, 25, 0x5a3027, 0.16);
  const head = scene.add.circle(0, y, 24, skin).setStrokeStyle(2, 0x8a503e, 0.22);
  const features = scene.add.graphics();
  features.fillStyle(0x342826).fillCircle(-7, y - 1, 2).fillCircle(7, y - 1, 2);
  features.lineStyle(2, 0x7c4437, 0.9).beginPath().arc(0, y + 4, 7, 0.25, Math.PI - 0.25).strokePath();
  return scene.add.container(0, 0, [headShadow, head, features]);
}

export function createCafeBackdrop(scene: Phaser.Scene): void {
  scene.add.image(0, 0, 'cafe-day1-background-v2').setOrigin(0).setDisplaySize(1280, 720).setDepth(0);

  const light = scene.add.graphics().setDepth(1);
  light.fillStyle(0xffe2a0, 0.07).fillTriangle(120, 185, 430, 720, 780, 720);
  light.fillStyle(0xffd98a, 0.055).fillEllipse(1035, 215, 390, 260);
  light.fillStyle(0x092824, 0.08).fillRect(0, 0, 1280, 46);

  const edgeShade = scene.add.graphics().setDepth(2);
  edgeShade.fillStyle(0x061d1b, 0.11).fillRect(0, 0, 20, 720).fillRect(1260, 0, 20, 720);
  edgeShade.fillStyle(0x061d1b, 0.09).fillRect(0, 690, 1280, 30);
}

export function createCafeForeground(scene: Phaser.Scene): void {
  scene.add.image(0, 0, 'cafe-day1-background-v2')
    .setOrigin(0).setDisplaySize(1280, 720).setCrop(988, 305, 684, 255).setDepth(24);

  const foreground = scene.add.graphics().setDepth(42);
  foreground.fillStyle(0x0c302c, 0.17).fillEllipse(36, 685, 190, 54);
  foreground.fillStyle(0x0c302c, 0.12).fillEllipse(1225, 690, 220, 50);
}

export function createProgressDecor(scene: Phaser.Scene, stage: number, menu: MenuItemId[]): void {
  if (stage < 2) return;
  const board = scene.add.container(744, 188).setDepth(7);
  const shadow = scene.add.graphics().fillStyle(0x071f1d, 0.28).fillRoundedRect(-71, -43, 142, 91, 8);
  const frame = scene.add.graphics()
    .fillStyle(0x8f5c35).fillRoundedRect(-74, -47, 148, 94, 8)
    .fillStyle(0x173f3a).fillRoundedRect(-65, -38, 130, 76, 4);
  const title = scene.add.text(0, -27, 'BUGÜN', { fontFamily: UI_FONT, fontSize: '10px', fontStyle: 'bold', color: '#efca78', letterSpacing: 2 }).setOrigin(0.5);
  const items = scene.add.text(0, 4, menu.map((id) => MENU_ITEMS[id].shortCode).join('  ·  '), {
    fontFamily: UI_FONT, fontSize: '9px', color: '#fff0ce', align: 'center', wordWrap: { width: 116 }, lineSpacing: 3,
  }).setOrigin(0.5, 0).setY(-12);
  board.add([shadow, frame, title, items]);
  if (stage >= 3) {
    const garland = scene.add.graphics().setDepth(6);
    garland.lineStyle(2, 0xd7a753, 0.7).beginPath().moveTo(525, 88).lineTo(610, 110).lineTo(745, 110).lineTo(830, 88).strokePath();
    for (let index = 0; index < 7; index += 1) {
      garland.fillStyle(index % 2 ? 0xd5674f : 0xe9c66e, 0.85).fillCircle(548 + index * 43, 96 + (index % 2) * 8, 5);
    }
  }
}

function createYaseminSprite(scene: Phaser.Scene): Phaser.GameObjects.Container {
  const legs = scene.add.graphics()
    .fillStyle(0x243b3b).lineStyle(2, 0x172928, 0.3)
    .fillRoundedRect(-17, 7, 13, 31, 6).strokeRoundedRect(-17, 7, 13, 31, 6)
    .fillRoundedRect(4, 7, 13, 31, 6).strokeRoundedRect(4, 7, 13, 31, 6)
    .fillStyle(0x55362e).fillEllipse(-10, 38, 22, 9).fillEllipse(12, 38, 22, 9);
  const body = scene.add.graphics()
    .fillStyle(0x9d3e34, 0.18).fillRoundedRect(-29, -40, 59, 65, 20)
    .fillStyle(0xd55e46).lineStyle(2, 0x9d493a, 0.45)
    .fillRoundedRect(-28, -43, 56, 65, 20).strokeRoundedRect(-28, -43, 56, 65, 20);
  const arms = scene.add.graphics().fillStyle(0xe1a37e).fillRoundedRect(-34, -27, 11, 42, 6).fillRoundedRect(23, -27, 11, 42, 6);
  const apron = scene.add.graphics()
    .fillStyle(0xfff0cb).lineStyle(2, 0xcfaa73, 0.48)
    .fillRoundedRect(-19, -23, 38, 45, 9).strokeRoundedRect(-19, -23, 38, 45, 9)
    .lineStyle(2, 0xcfaa73, 0.55).lineBetween(-18, -10, 18, -10);
  const face = createFace(scene, 0xe6ad86, -64);
  const hair = scene.add.graphics()
    .fillStyle(0x3b2526).fillEllipse(0, -74, 53, 38).fillCircle(-21, -60, 13).fillCircle(20, -76, 8)
    .fillStyle(0x6d4135, 0.28).fillEllipse(-7, -82, 24, 8);
  const hairClip = scene.add.circle(17, -78, 4, 0xefc668);
  return scene.add.container(0, 0, [legs, body, arms, apron, face, hair, hairClip]);
}

export function createYasemin(scene: Phaser.Scene, x: number, y: number): Phaser.GameObjects.Container {
  const shadow = scene.add.ellipse(0, 38, 64, 18, 0x102623, 0.27);
  const sprite = scene.textures.exists('yasemin-production')
    ? createProductionCharacterSprite(scene, 'yasemin', 0.86, 40, 'idle')
    : createYaseminSprite(scene);
  const ui = scene.add.container(0, 0, [createNameBadge(scene, 'YASEMİN', 57)]);
  const tray = scene.add.container(32, -13).setVisible(false);
  const trayShadow = scene.add.ellipse(2, 3, 48, 13, 0x422b25, 0.25);
  const trayPlate = scene.add.ellipse(0, 0, 47, 12, 0xc99b60).setStrokeStyle(2, 0x6f4835);
  const trayIcon = scene.add.text(0, -14, '☕', { fontFamily: UI_FONT, fontSize: '20px' }).setOrigin(0.5);
  tray.add([trayShadow, trayPlate, trayIcon]);
  ui.add(tray);
  const trayStatusPlate = scene.add.graphics().fillStyle(0x173f3a, 0.96).lineStyle(1, 0xe8c575, 0.8).fillRoundedRect(-36, -10, 72, 22, 8).strokeRoundedRect(-36, -10, 72, 22, 8);
  const trayStatusText = scene.add.text(0, 1, '', { fontFamily: UI_FONT, fontSize: '9px', fontStyle: 'bold', color: '#fff1cb' }).setOrigin(0.5);
  const trayStatus = scene.add.container(0, 82, [trayStatusPlate, trayStatusText]).setVisible(false);
  ui.add(trayStatus);
  const container = scene.add.container(x, y, [shadow, sprite, ui]).setDepth(30);
  container.setData('sprite', sprite).setData('ui', ui).setData('tray', tray).setData('trayStatus', trayStatus).setData('trayStatusText', trayStatusText).setData('body', sprite);
  container.setData('characterState', 'idle').setData('direction', 'down').setData('productionArt', sprite instanceof Phaser.GameObjects.Sprite);
  if (!container.getData('productionArt')) scene.tweens.add({ targets: sprite, y: -2, duration: 920, yoyo: true, repeat: -1, ease: 'Sine.inOut' });
  return container;
}

function createFevziSprite(scene: Phaser.Scene): Phaser.GameObjects.Container {
  const legs = scene.add.graphics()
    .fillStyle(0x263b3a).fillRoundedRect(-15, 5, 11, 29, 5).fillRoundedRect(4, 5, 11, 29, 5)
    .fillStyle(0x4e352e).fillEllipse(-10, 34, 20, 8).fillEllipse(10, 34, 20, 8);
  const torso = scene.add.graphics()
    .fillStyle(0x235c57, 0.2).fillRoundedRect(-27, -40, 56, 61, 19)
    .fillStyle(0x34786e).lineStyle(2, 0x20584f, 0.45)
    .fillRoundedRect(-25, -43, 50, 61, 18).strokeRoundedRect(-25, -43, 50, 61, 18);
  const arms = scene.add.graphics().fillStyle(0xd89d76).fillRoundedRect(-31, -24, 10, 37, 6).fillRoundedRect(21, -24, 10, 37, 6);
  const apron = scene.add.graphics()
    .fillStyle(0xdba84f).lineStyle(2, 0x9a6c2f, 0.45)
    .fillRoundedRect(-17, -21, 34, 41, 8).strokeRoundedRect(-17, -21, 34, 41, 8);
  const face = createFace(scene, 0xd9a17d, -63);
  const hair = scene.add.graphics().fillStyle(0x2f2525).fillEllipse(0, -74, 48, 30).fillCircle(19, -68, 8);
  const towel = scene.add.graphics().fillStyle(0xf4e3bc).fillRoundedRect(20, -12, 9, 30, 4);
  return scene.add.container(0, 0, [legs, torso, arms, apron, face, hair, towel]);
}

export function createFevzi(scene: Phaser.Scene, x: number, y: number): Phaser.GameObjects.Container {
  const shadow = scene.add.ellipse(0, 26, 52, 15, 0x102623, 0.18);
  const sprite = scene.textures.exists('fevzi-production')
    ? createProductionCharacterSprite(scene, 'fevzi', 0.84, 34, 'idleBehindCounter')
    : createFevziSprite(scene);
  const actionPlate = scene.add.graphics()
    .fillStyle(0x9b4e3c, 0.98).lineStyle(1, 0xffdd8b, 0.85)
    .fillRoundedRect(-58, -13, 116, 26, 10).strokeRoundedRect(-58, -13, 116, 26, 10);
  const actionText = scene.add.text(0, 0, '', { fontFamily: UI_FONT, fontSize: '9px', fontStyle: 'bold', color: '#fff3cf', letterSpacing: 1 }).setOrigin(0.5);
  const action = scene.add.container(0, -142, [actionPlate, actionText]).setVisible(false);
  const ui = scene.add.container(0, 0, [createNameBadge(scene, 'FEVZİ', -108), action]);
  const container = scene.add.container(x, y, [shadow, sprite, ui]).setDepth(18);
  container.setData('sprite', sprite).setData('ui', ui).setData('action', action).setData('actionText', actionText);
  container.setData('characterState', 'idleBehindCounter').setData('direction', 'left').setData('productionArt', sprite instanceof Phaser.GameObjects.Sprite);
  setCharacterFacing(container, -1);
  if (!container.getData('productionArt')) scene.tweens.add({ targets: sprite, y: -3, duration: 1350, yoyo: true, repeat: -1, ease: 'Sine.inOut' });
  return container;
}

export function setFevziAction(container: Phaser.GameObjects.Container, label: string): void {
  const action = container.getData('action') as Phaser.GameObjects.Container | undefined;
  const text = container.getData('actionText') as Phaser.GameObjects.Text | undefined;
  text?.setText(label);
  action?.setVisible(Boolean(label));
}

function createCustomerSprite(
  scene: Phaser.Scene,
  archetype: CustomerArchetype,
  color: number,
): { sprite: Phaser.GameObjects.Container; legs: Phaser.GameObjects.Graphics; expression: Phaser.GameObjects.Text } {
  const legs = scene.add.graphics()
    .fillStyle(0x3f3635).fillRoundedRect(-15, 5, 11, 29, 5).fillRoundedRect(4, 5, 11, 29, 5)
    .fillStyle(0x4f352d).fillEllipse(-10, 34, 19, 8).fillEllipse(10, 34, 19, 8);
  const body = scene.add.graphics()
    .fillStyle(0x64382e, 0.15).fillRoundedRect(-27, -37, 56, 58, 18)
    .fillStyle(color).lineStyle(2, 0x754a3e, 0.3)
    .fillRoundedRect(-25, -40, 50, 58, 18).strokeRoundedRect(-25, -40, 50, 58, 18);
  const skin = archetype === 'GENEROUS' ? 0xd59c76 : archetype === 'IMPATIENT' ? 0xdca17e : 0xe2ad88;
  const arms = scene.add.graphics().fillStyle(skin).fillRoundedRect(-30, -24, 9, 35, 5).fillRoundedRect(21, -24, 9, 35, 5);
  const accent = scene.add.graphics();
  if (archetype === 'PATIENT') accent.fillStyle(0xf3d7a0).fillRoundedRect(-22, -24, 44, 7, 4);
  else if (archetype === 'GENEROUS') accent.lineStyle(4, 0xf1c461).lineBetween(-16, -31, 13, 7).lineBetween(16, -31, -13, 7);
  else if (archetype === 'IMPATIENT') accent.fillStyle(0xf2c86e).fillTriangle(-8, -29, 9, -29, 0, -13);
  else accent.fillStyle(0xf4e4c5, 0.85).fillCircle(0, -26, 4);
  const face = createFace(scene, archetype === 'GENEROUS' ? 0xd69e78 : 0xe5af89, -59);
  const hairColor = archetype === 'PATIENT' ? 0xad6747 : archetype === 'GENEROUS' ? 0x332729 : archetype === 'IMPATIENT' ? 0x713c49 : 0x42302d;
  const hair = scene.add.graphics().fillStyle(hairColor).fillEllipse(0, -69, 47, 30).fillCircle(archetype === 'PATIENT' ? -18 : 18, -62, 8);
  const expression = scene.add.text(0, -58, '', { fontFamily: UI_FONT, fontSize: '16px', fontStyle: 'bold', color: '#8e4337' }).setOrigin(0.5);
  const sprite = scene.add.container(0, 0, [legs, body, arms, accent, face, hair, expression]);
  return { sprite, legs, expression };
}

function createOrderBubble(scene: Phaser.Scene): Phaser.GameObjects.Container {
  const shadow = scene.add.graphics().fillStyle(0x0b2825, 0.2).fillRoundedRect(-55, -29, 110, 62, 20);
  const paper = scene.add.graphics()
    .fillStyle(0xfff6dc).lineStyle(3, 0x1c4d47, 1)
    .fillRoundedRect(-55, -34, 110, 61, 20).strokeRoundedRect(-55, -34, 110, 61, 20)
    .fillTriangle(-11, 26, 5, 26, -2, 38);
  const inner = scene.add.graphics().lineStyle(1, 0xd4a958, 0.75).strokeRoundedRect(-48, -27, 96, 47, 15);
  const symbol = scene.add.text(0, -4, '…', {
    fontFamily: UI_FONT, fontSize: '24px', fontStyle: 'bold', color: '#173f3a',
  }).setOrigin(0.5);

  const itemIcons = new Map<MenuItemId, Phaser.GameObjects.Container>();
  const iconChildren: Phaser.GameObjects.GameObject[] = [];
  (Object.keys(MENU_ITEMS) as MenuItemId[]).forEach((id) => {
    const item = MENU_ITEMS[id];
    const disk = scene.add.circle(0, -5, 17, item.color).setStrokeStyle(2, 0xffe6a4, 0.9);
    const code = scene.add.text(0, -5, item.shortCode, {
      fontFamily: UI_FONT, fontSize: item.shortCode.length > 3 ? '7px' : '8px', fontStyle: 'bold', color: '#fff7dc',
    }).setOrigin(0.5);
    const icon = scene.add.container(0, 0, [disk, code]).setVisible(false);
    itemIcons.set(id, icon);
    iconChildren.push(icon);
  });

  const bubble = scene.add.container(0, -130, [shadow, paper, inner, symbol, ...iconChildren]).setVisible(false);
  bubble.setData('symbol', symbol).setData('itemIcons', itemIcons).setData('lastState', '');
  return bubble;
}

export function setOrderBubbleItems(bubble: Phaser.GameObjects.Container, items: MenuItemId[]): void {
  const icons = bubble.getData('itemIcons') as Map<MenuItemId, Phaser.GameObjects.Container>;
  icons.forEach((icon) => icon.setVisible(false));
  items.slice(0, 2).forEach((id, index) => {
    const icon = icons.get(id);
    if (!icon) return;
    icon.setVisible(true).setScale(items.length > 1 ? 0.82 : 1).setPosition(items.length > 1 ? (index === 0 ? -22 : 22) : 0, -1);
  });
}

export function createCustomerVisual(
  scene: Phaser.Scene,
  x: number,
  y: number,
  archetype: CustomerArchetype,
  color: number,
  visualId?: CustomerVisualId,
): Phaser.GameObjects.Container {
  const shadow = scene.add.ellipse(0, 34, 55, 16, 0x102623, 0.24);
  const placeholder = createCustomerSprite(scene, archetype, color);
  const productionSprite = visualId && scene.textures.exists(`customer-${visualId}`)
    ? createProductionCustomerSprite(scene, visualId)
    : undefined;
  const sprite = productionSprite ?? placeholder.sprite;
  if (productionSprite) placeholder.sprite.setVisible(false);
  const bubble = createOrderBubble(scene);
  const patienceShell = scene.add.graphics()
    .fillStyle(0x102f2c, 0.86).fillRoundedRect(-36, -103, 72, 12, 6)
    .lineStyle(1, 0xf2d490, 0.55).strokeRoundedRect(-36, -103, 72, 12, 6);
  const patienceFill = scene.add.rectangle(-32, -97, 64, 6, 0x70c58e).setOrigin(0, 0.5);
  const archetypeMark = scene.add.text(0, -112, archetype === 'GENEROUS' ? '★' : archetype === 'PATIENT' ? '✿' : archetype === 'IMPATIENT' ? '⚡' : '•', {
    fontFamily: UI_FONT, fontSize: '12px', color: '#f4cb70', stroke: '#173f3a', strokeThickness: 3,
  }).setOrigin(0.5);
  const ui = scene.add.container(0, 0, [patienceShell, patienceFill, archetypeMark, bubble]);
  const container = scene.add.container(x, y, [shadow, sprite, ui]).setDepth(28);
  const hitArea = new Phaser.Geom.Rectangle(0, -39.5, 108, 158);
  container.setSize(108, 158).setInteractive(hitArea, Phaser.Geom.Rectangle.Contains);
  if (container.input) container.input.cursor = 'pointer';
  container.setData('interactionHitArea', new Phaser.Geom.Rectangle(-54, -118.5, 108, 158));
  container.setData('sprite', sprite).setData('ui', ui).setData('bubble', bubble);
  container.setData('visualLocalBounds', new Phaser.Geom.Rectangle(-58, -112, 116, 150));
  container.setData('bubbleText', bubble.getData('symbol')).setData('patienceFill', patienceFill);
  container.setData('body', sprite).setData('legs', placeholder.legs).setData('expression', placeholder.expression).setData('isSeated', false);
  container.setData('customerVisualId', visualId ?? 'placeholder').setData('productionCustomerArt', Boolean(productionSprite));
  if (!productionSprite) scene.tweens.add({ targets: sprite, y: -2, duration: 900 + Math.random() * 250, yoyo: true, repeat: -1, ease: 'Sine.inOut' });
  return container;
}

export function createGorkemVisual(scene: Phaser.Scene, x: number, y: number): Phaser.GameObjects.Container {
  const shadow = scene.add.ellipse(0, 35, 60, 17, 0x102623, 0.25);
  const legs = scene.add.graphics()
    .fillStyle(0x2c3547).fillRoundedRect(-16, 6, 12, 29, 5).fillRoundedRect(4, 6, 12, 29, 5)
    .fillStyle(0xf0b84d).fillEllipse(-10, 35, 21, 8).fillEllipse(10, 35, 21, 8);
  const body = scene.add.graphics()
    .fillStyle(0x65518c).lineStyle(2, 0x493a69, 0.6).fillRoundedRect(-27, -40, 54, 61, 18).strokeRoundedRect(-27, -40, 54, 61, 18)
    .fillStyle(0xf0c25d).fillRoundedRect(-24, -22, 48, 8, 4);
  const arms = scene.add.graphics().fillStyle(0xdca17d).fillRoundedRect(-32, -24, 10, 37, 6).fillRoundedRect(22, -24, 10, 37, 6);
  const face = createFace(scene, 0xdfa681, -62);
  const hair = scene.add.graphics().fillStyle(0x2d2732).fillEllipse(0, -72, 49, 31).fillCircle(-19, -66, 8);
  const cap = scene.add.graphics().fillStyle(0xf0b84d).fillEllipse(4, -80, 40, 15).fillRoundedRect(3, -81, 28, 5, 2);
  const waveHand = scene.add.circle(32, -35, 7, 0xdca17d).setVisible(false);
  const placeholder = scene.add.container(0, 0, [legs, body, arms, face, hair, cap, waveHand]);
  const sprite = scene.textures.exists('gorkem-production')
    ? createProductionCharacterSprite(scene, 'gorkem', 0.70, 37, 'idle')
    : placeholder;
  if (sprite !== placeholder) placeholder.setVisible(false);
  const bubble = createOrderBubble(scene);
  bubble.y = -155;
  const ui = scene.add.container(0, 0, [createNameBadge(scene, 'GÖRKEM', -105), bubble]);
  const hitArea = new Phaser.Geom.Rectangle(0, -43, 92, 166);
  const container = scene.add.container(x, y, [shadow, sprite, ui]).setDepth(29).setSize(92, 166).setInteractive(hitArea, Phaser.Geom.Rectangle.Contains);
  if (container.input) container.input.cursor = 'pointer';
  container.setData('interactionHitArea', new Phaser.Geom.Rectangle(-46, -126, 92, 166));
  container.setData('sprite', sprite).setData('ui', ui).setData('bubble', bubble).setData('waveHand', waveHand).setData('legs', legs);
  container.setData('characterState', 'idle').setData('direction', 'down').setData('productionArt', sprite instanceof Phaser.GameObjects.Sprite);
  if (!container.getData('productionArt')) scene.tweens.add({ targets: sprite, y: -2, duration: 780, yoyo: true, repeat: -1, ease: 'Sine.inOut' });
  return container;
}

export function setGorkemBubble(container: Phaser.GameObjects.Container, label: string, item?: MenuItemId): void {
  const bubble = container.getData('bubble') as Phaser.GameObjects.Container;
  const symbol = bubble.getData('symbol') as Phaser.GameObjects.Text;
  setOrderBubbleItems(bubble, item ? [item] : []);
  symbol.setVisible(!item).setText(label);
  bubble.setVisible(Boolean(label || item)).setAlpha(1).setScale(1);
}

export function setGorkemSeated(container: Phaser.GameObjects.Container, seated: boolean): void {
  if (container.getData('productionArt')) {
    setProductionCharacterState(container, seated ? 'seatedIdle' : 'idle', 'down');
    return;
  }
  (container.getData('legs') as Phaser.GameObjects.Graphics).setVisible(!seated);
  container.setScale(seated ? 0.88 : 1);
}

export function setGorkemWaving(container: Phaser.GameObjects.Container, waving: boolean): void {
  if (container.getData('productionArt')) {
    setProductionCharacterState(container, waving ? 'wave' : 'idle', 'down');
    return;
  }
  const hand = container.getData('waveHand') as Phaser.GameObjects.Arc;
  hand.setVisible(waving);
  if (waving) sceneTweenWave(container.scene, hand);
}

function sceneTweenWave(scene: Phaser.Scene, hand: Phaser.GameObjects.Arc): void {
  scene.tweens.add({ targets: hand, angle: 24, y: '-=8', duration: 180, yoyo: true, repeat: 4, ease: 'Sine.inOut' });
}

export function createGorkemSideTable(scene: Phaser.Scene): void {
  const shadow = scene.add.ellipse(1120, 525, 125, 28, 0x102623, 0.24).setDepth(19);
  const leg = scene.add.rectangle(1120, 515, 15, 63, 0x5b3b2d).setDepth(20);
  const top = scene.add.ellipse(1120, 483, 125, 55, 0x9c6649).setStrokeStyle(5, 0x603e31).setDepth(27);
  const flower = scene.add.text(1120, 470, '✿', { fontFamily: UI_FONT, fontSize: '17px', color: '#efc85e', stroke: '#256059', strokeThickness: 3 }).setOrigin(0.5).setDepth(28);
  shadow.setData('decor', true); leg.setData('decor', true); top.setData('decor', true); flower.setData('decor', true);
}

export function setCharacterFacing(container: Phaser.GameObjects.Container, direction: -1 | 1): void {
  if (setProductionCustomerDirection(container, direction < 0 ? 'left' : 'right')) {
    container.setData('facing', direction);
    return;
  }
  if (setProductionCharacterDirection(container, direction < 0 ? 'left' : 'right')) {
    container.setData('facing', direction);
    return;
  }
  const sprite = container.getData('sprite') as Phaser.GameObjects.Container | undefined;
  if (sprite) sprite.setScale(direction, 1);
  const tray = container.getData('tray') as Phaser.GameObjects.Container | undefined;
  if (tray) tray.x = 32 * direction;
  container.setData('facing', direction);
}

export function setCharacterDirection(container: Phaser.GameObjects.Container, direction: CharacterDirection): void {
  if (!setProductionCharacterDirection(container, direction)) setCharacterFacing(container, direction === 'left' ? -1 : 1);
  container.setData('direction', direction).setData('facing', direction === 'left' ? -1 : 1);
}

export function setCharacterAnimation(
  container: Phaser.GameObjects.Container,
  state: string,
  direction?: CharacterDirection,
  ignoreIfPlaying = false,
): boolean {
  return setProductionCharacterState(container, state, direction, ignoreIfPlaying);
}

export function setCustomerSeated(container: Phaser.GameObjects.Container, seated: boolean): void {
  if (container.getData('productionCustomerArt')) {
    setProductionCustomerState(container, seated ? 'seatedIdle' : 'idle', 'down');
    container.setData('isSeated', seated);
    return;
  }
  const legs = container.getData('legs') as Phaser.GameObjects.Graphics | undefined;
  const sprite = container.getData('sprite') as Phaser.GameObjects.Container | undefined;
  legs?.setVisible(!seated);
  if (sprite) sprite.y = seated ? 8 : 0;
  container.setData('isSeated', seated);
}

export function setCustomerReaction(container: Phaser.GameObjects.Container, reaction: 'neutral' | 'happy' | 'angry'): void {
  if (container.getData('productionCustomerArt')) {
    setProductionCustomerState(container, reaction === 'neutral' ? 'seatedIdle' : reaction);
    return;
  }
  const expression = container.getData('expression') as Phaser.GameObjects.Text | undefined;
  if (!expression) return;
  expression.setText(reaction === 'happy' ? '⌣' : reaction === 'angry' ? '⌢' : '');
  expression.setColor(reaction === 'happy' ? '#2f7762' : '#a33f36');
}

export function createTableVisual(scene: Phaser.Scene, x: number, y: number, id: number): TableVisual {
  const glow = scene.add.ellipse(0, 9, 194, 104, 0xffd674, 0).setStrokeStyle(4, 0xffe6a4, 0);
  const floorShadow = scene.add.ellipse(3, 48, 164, 54, 0x3d261f, 0.22);
  const chairs = scene.add.graphics()
    .fillStyle(0x245c56).lineStyle(3, 0x173e3a, 0.6)
    .fillRoundedRect(-78, -5, 34, 63, 14).strokeRoundedRect(-78, -5, 34, 63, 14)
    .fillRoundedRect(44, -5, 34, 63, 14).strokeRoundedRect(44, -5, 34, 63, 14)
    .fillStyle(0x34766b).fillRoundedRect(-73, 2, 24, 36, 10).fillRoundedRect(49, 2, 24, 36, 10);
  const pedestal = scene.add.graphics()
    .fillStyle(0x4c2d26).fillRoundedRect(-8, 15, 16, 49, 6)
    .fillStyle(0x63382b).fillEllipse(0, 63, 73, 22);
  const rim = scene.add.ellipse(0, 2, 174, 84, 0x5d3428).setStrokeStyle(3, 0x3c251f, 0.7);
  const surface = scene.add.ellipse(0, -2, 160, 72, 0xa95f3f).setStrokeStyle(3, 0xe7b86e, 0.65);
  const innerSurface = scene.add.ellipse(0, -5, 146, 60, 0xc6764e, 0.95);
  const highlight = scene.add.arc(-19, -16, 51, 205, 335, false, 0, 0).setStrokeStyle(3, 0xf2bd78, 0.25);
  const placeSettings = scene.add.container(0, -4, [
    scene.add.ellipse(-45, -1, 25, 12, 0xf4e4c7).setStrokeStyle(2, 0xb48b61, 0.75),
    scene.add.ellipse(45, -1, 25, 12, 0xf4e4c7).setStrokeStyle(2, 0xb48b61, 0.75),
    scene.add.circle(-45, -2, 4, 0xd49b55),
    scene.add.circle(45, -2, 4, 0xd49b55),
  ]);
  const vase = scene.add.container(0, -5, [
    scene.add.graphics().fillStyle(0xf0d5a4).fillRoundedRect(-6, -13, 12, 20, 5),
    scene.add.graphics().lineStyle(2, 0x48775a).lineBetween(0, -10, -7, -23).lineBetween(0, -10, 7, -25),
    scene.add.circle(-8, -24, 6, 0xe9b84e), scene.add.circle(7, -26, 6, 0xcf654b),
  ]);
  const number = scene.add.text(0, 18, `${id}`, { fontFamily: UI_FONT, fontSize: '9px', fontStyle: 'bold', color: '#6b382a' }).setOrigin(0.5);
  const crumbs = scene.add.container(0, -5, [
    scene.add.circle(-27, 2, 4, 0x6f3e2f), scene.add.circle(17, 8, 3, 0x6f3e2f), scene.add.circle(31, -5, 3, 0x6f3e2f),
    scene.add.text(1, -3, '≈', { fontFamily: UI_FONT, fontSize: '30px', color: '#7a4937' }).setOrigin(0.5),
  ]).setVisible(false);

  const stateBadge = scene.add.container(0, -75);
  const badgeShadow = scene.add.graphics().fillStyle(0x102f2b, 0.22).fillRoundedRect(-53, -13, 106, 31, 12);
  const statePlate = scene.add.graphics();
  const stateIcon = scene.add.text(-36, 1, '✦', { fontFamily: UI_FONT, fontSize: '14px', fontStyle: 'bold', color: '#efc667' }).setOrigin(0.5);
  const stateText = scene.add.text(9, 1, 'HAZIR', { fontFamily: UI_FONT, fontSize: '10px', fontStyle: 'bold', color: '#fff2cd', letterSpacing: 1 }).setOrigin(0.5);
  stateBadge.add([badgeShadow, statePlate, stateIcon, stateText]);

  const container = scene.add.container(x, y, [glow, floorShadow, chairs, pedestal, rim, surface, innerSurface, highlight, placeSettings, vase, number, crumbs, stateBadge]);
  const hitArea = new Phaser.Geom.Rectangle(0, 0, 194, 145);
  container.setDepth(8 + y / 20).setSize(194, 145).setInteractive(hitArea, Phaser.Geom.Rectangle.Contains);
  if (container.input) container.input.cursor = 'pointer';
  container.setData('interactionHitArea', new Phaser.Geom.Rectangle(-97, -72.5, 194, 145));
  return { container, glow, surface, stateBadge, statePlate, stateIcon, stateText, crumbs };
}

function setTableBadge(table: TableVisual, icon: string, label: string, color: number, visible = true): void {
  table.stateBadge.setVisible(visible).setAlpha(visible ? 1 : 0);
  table.statePlate.clear()
    .fillStyle(color, 0.96).lineStyle(2, 0xf1cc7b, 0.72)
    .fillRoundedRect(-53, -16, 106, 31, 12).strokeRoundedRect(-53, -16, 106, 31, 12);
  table.stateIcon.setText(icon);
  table.stateText.setText(label);
}

export function updateTableVisual(table: TableVisual, state: TableState, selected = false): void {
  if (state === 'EMPTY') setTableBadge(table, selected ? '↘' : '✦', selected ? 'MASA SEÇ' : 'HAZIR', selected ? 0xa4563e : 0x285e56);
  if (state === 'ASSIGNED') setTableBadge(table, '•', 'AYRILDI', 0xa97435);
  if (state === 'OCCUPIED') setTableBadge(table, '…', 'MENÜ', 0x385f58);
  if (state === 'WAITING_FOR_CLEANUP') setTableBadge(table, '✦', 'TEMİZLE', 0xa54e40);
  table.crumbs.setVisible(state === 'WAITING_FOR_CLEANUP');
  table.glow.setFillStyle(0xffd674, selected ? 0.18 : 0).setStrokeStyle(4, 0xffe6a4, selected ? 0.9 : 0);
  table.surface.setFillStyle(state === 'WAITING_FOR_CLEANUP' ? 0x98523e : 0xa95f3f);
}

export function updateTableServiceState(table: TableVisual, state: CustomerState): void {
  const serviceStates: Partial<Record<CustomerState, [string, string, number]>> = {
    DECIDING: ['…', 'SEÇİYOR', 0x385f58],
    WAITING_TO_ORDER: ['✎', 'SİPARİŞ', 0xa65c3e],
    WAITING_FOR_FOOD: ['◷', 'HAZIRLANIYOR', 0x8a6634],
    EATING: ['♥', 'AFİYET', 0x3e7160],
    WAITING_TO_PAY: ['₺', 'ÖDEME', 0x9a5839],
  };
  const config = serviceStates[state];
  if (!config) return;
  setTableBadge(table, config[0], config[1], config[2]);
}
